import { LocalDate } from "@js-joda/core";
import { type Order } from "model/Analytics";
import { type TimeReportDto } from "model/TimeReport";
import {
  type AnalyticsPeriod,
  type OperatingHours,
  getBusinessDayOfWeek,
  getOperatingHoursList,
  getWeekBounds,
  jsDayToBucketIndex,
  parseTimestamp,
  walkTimeReportSlots,
} from "./analytics.operatingHours";
import { type CustomCost, getCustomCostsTotal } from "./analytics.customCosts";
import { type CustomEarning, getCustomEarningsTotal } from "./analytics.customEarnings";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ChartBucket {
  label: string;
  sales: number;
  tips: number;
  personnelCost: number;
  result: number;
}

export interface BuildChartDataParams {
  orders: Order[];
  timeReports: TimeReportDto[];
  period: AnalyticsPeriod;
  selectedDate: LocalDate;
  operatingHours: OperatingHours;
  /** Translated short weekday labels (Mon–Sun), used as bucket labels in week view */
  weekdayLabels: string[];
  /** Actual total from the API — chart buckets are scaled so they sum to this value exactly */
  totalPersonnelCost: number | null;
  /** Custom costs — period costs distributed evenly; date costs attributed to their specific bucket */
  customCosts: CustomCost[];
  /** Custom earnings — each attributed to its specific date bucket */
  customEarnings: CustomEarning[];
}

// ─── Bucket builders ──────────────────────────────────────────────────────────

const buildDayBuckets = (
  orders: Order[],
  timeReports: TimeReportDto[],
  selectedDate: LocalDate,
  operatingHours: OperatingHours,
): ChartBucket[] => {
  // joda DayOfWeek.value(): 1=Mon…7=Sun → bucket index 0–6
  const bucketIdx = selectedDate.dayOfWeek().value() - 1;
  const hoursList = getOperatingHoursList(operatingHours[bucketIdx]);
  const hourToIndex = new Map(hoursList.map((h, i) => [h, i]));

  const buckets: ChartBucket[] = hoursList.map((hour) => ({
    label: `${String(hour).padStart(2, "0")}:00`,
    sales: 0,
    tips: 0,
    personnelCost: 0,
    result: 0,
  }));

  for (const order of orders) {
    const d = parseTimestamp(order.timestamp);
    const idx = hourToIndex.get(d.getHours());
    if (idx === undefined) continue;
    buckets[idx].sales += order.amount;
    buckets[idx].tips += order.tip;
  }

  for (const report of timeReports) {
    walkTimeReportSlots(report, (slotStart, fractionHours) => {
      const idx = hourToIndex.get(slotStart.getHours());
      if (idx !== undefined) {
        buckets[idx].personnelCost += report.hourlyRate * fractionHours;
      }
    });
  }

  return buckets;
};

const buildWeekBuckets = (
  orders: Order[],
  timeReports: TimeReportDto[],
  weekdayLabels: string[],
  operatingHours: OperatingHours,
): ChartBucket[] => {
  const buckets: ChartBucket[] = Array.from({ length: 7 }, (_, i) => ({
    label: weekdayLabels[i],
    sales: 0,
    tips: 0,
    personnelCost: 0,
    result: 0,
  }));

  for (const order of orders) {
    const d = parseTimestamp(order.timestamp);
    const businessJsDay = getBusinessDayOfWeek(d, operatingHours);
    if (businessJsDay === null) continue;
    buckets[jsDayToBucketIndex(businessJsDay)].sales += order.amount;
    buckets[jsDayToBucketIndex(businessJsDay)].tips += order.tip;
  }

  for (const report of timeReports) {
    walkTimeReportSlots(report, (slotStart, fractionHours) => {
      const businessJsDay = getBusinessDayOfWeek(slotStart, operatingHours);
      if (businessJsDay !== null) {
        buckets[jsDayToBucketIndex(businessJsDay)].personnelCost +=
          report.hourlyRate * fractionHours;
      }
    });
  }

  return buckets;
};

// ─── Main entry point ─────────────────────────────────────────────────────────

export const buildChartData = (params: BuildChartDataParams): ChartBucket[] => {
  const {
    orders,
    timeReports,
    period,
    selectedDate,
    operatingHours,
    weekdayLabels,
    totalPersonnelCost,
    customCosts,
    customEarnings,
  } = params;

  const buckets =
    period === "week"
      ? buildWeekBuckets(orders, timeReports, weekdayLabels, operatingHours)
      : buildDayBuckets(orders, timeReports, selectedDate, operatingHours);

  // Scale raw personnelCost (hourlyRate × hours) so the bucket sum equals
  // the stat card value exactly, regardless of how the API computes total cost.
  if (totalPersonnelCost !== null) {
    const rawTotal = buckets.reduce((sum, b) => sum + b.personnelCost, 0);
    if (rawTotal > 0) {
      const scale = totalPersonnelCost / rawTotal;
      for (const bucket of buckets) {
        bucket.personnelCost *= scale;
      }
    }
  }

  // Base result from sales vs personnel cost.
  for (const bucket of buckets) {
    bucket.result = bucket.sales - bucket.personnelCost;
  }

  if (buckets.length === 0) return buckets;

  // ── Period costs: distribute evenly across all buckets ──────────────────────
  const periodCostsTotal = getCustomCostsTotal(
    customCosts.filter((c) => (c.kind ?? "period") === "period"),
    period,
    selectedDate,
  );
  if (periodCostsTotal > 0) {
    const costPerBucket = periodCostsTotal / buckets.length;
    buckets.forEach((b) => {
      b.result -= costPerBucket;
    });
  }

  // ── Date costs: attribute each cost to its specific bucket ──────────────────
  const dateCosts = customCosts.filter((c) => c.kind === "date" && c.date);
  if (period === "week") {
    const { weekStart, weekEnd } = getWeekBounds(selectedDate);
    for (const cost of dateCosts) {
      const costDate = LocalDate.parse(cost.date!);
      if (!costDate.isBefore(weekStart) && !costDate.isAfter(weekEnd)) {
        buckets[costDate.dayOfWeek().value() - 1].result -= cost.amount;
      }
    }
  } else {
    const dayDateCostsTotal = getCustomCostsTotal(dateCosts, "day", selectedDate);
    if (dayDateCostsTotal > 0) {
      const costPerBucket = dayDateCostsTotal / buckets.length;
      buckets.forEach((b) => {
        b.result -= costPerBucket;
      });
    }
  }

  // ── Earnings: attribute each earning to its specific bucket ─────────────────
  if (period === "week") {
    const { weekStart, weekEnd } = getWeekBounds(selectedDate);
    for (const earning of customEarnings) {
      const earningDate = LocalDate.parse(earning.date);
      if (!earningDate.isBefore(weekStart) && !earningDate.isAfter(weekEnd)) {
        buckets[earningDate.dayOfWeek().value() - 1].result += earning.amount;
      }
    }
  } else {
    const dayEarningsTotal = getCustomEarningsTotal(customEarnings, "day", selectedDate);
    if (dayEarningsTotal > 0) {
      const earningPerBucket = dayEarningsTotal / buckets.length;
      buckets.forEach((b) => {
        b.result += earningPerBucket;
      });
    }
  }

  return buckets;
};
